const { useCallback, useState } = wp.element;
import { useLoadScript, GoogleMap, MarkerClusterer, Autocomplete } from '@react-google-maps/api';
const { __ } = wp.i18n;

// Hooks
import useDebounce from '../utils/useDebounce';

// Components
import Pin from './Pin';
import Locate from './Locate';
import Dealers from './Dealers';
// Helpers
import { getMapLanguage } from '../utils/helpers';

const libraries = [ 'places', 'geometry' ];
const htmlElement = document.querySelector( 'html' );

const Map = ( { dealers, mapContainerStyle, options, defaultCenter } ) => {
	const lang = htmlElement.getAttribute( 'lang' );
	const { region, language } = getMapLanguage( lang );

	const { isLoaded, loadError } = useLoadScript( {
		googleMapsApiKey: 'AIzaSyDT0qrOaD_1NQSXi5rzJR9bYGuXVGJRz6E',
		libraries,
		region,
		language,
		version: '3.42.9', // Fixes google bug when clicking markers.
	} );

	const [ center, setCenter ] = useState( defaultCenter );
	const [ autocomplete, setAutocomplete ] = useState();
	const [ selectedMarker, setSelectedMarker ] = useState( null );
	const [ mapAPI, setMapAPI ] = useState();
	const [ mapInstance, setMapInstance ] = useState();
	const [ mapBounds, setMapBounds ] = useState( false );

	const onLoad = value => {
		setAutocomplete( value );
	};

	const debouncedCenter = useDebounce( center, 400 );

	// Return map bounds based on list of places
	const getMapBounds = ( maps, places ) => {
		const bounds = new maps.LatLngBounds();

		places.forEach( place => {
			bounds.extend( new maps.LatLng(
				place.lat,
				place.lng,
			) );
		} );

		return bounds;
	};

	const onPlaceChanged = () => {
		if ( autocomplete !== null ) {
			const place = autocomplete.getPlace();
			const { geometry } = place;

			if ( ! geometry ) {
				return;
			}

			if ( place.geometry.viewport ) {
				mapInstance.fitBounds( place.geometry.viewport );
			} else {
				mapInstance.setCenter( place.geometry.location );
				mapInstance.setZoom( 17 );
			}
		}
	};

	const apiHasLoaded = useCallback( map => {
		const bounds = getMapBounds( window.google.maps, dealers );
		setMapBounds( bounds );
		map.fitBounds( bounds );
		setMapInstance( map );
		setMapAPI( window.google.maps );
	}, [] );

	const renderClusterer = useCallback(
		clusterer => dealers.map( marker => {
			return <Pin key={ marker.id } data={ marker } setSelectedMarker={ setSelectedMarker } selectedMarker={ selectedMarker } clusterer={ clusterer } />;
		} ), [ dealers, selectedMarker ]
	);

	const panTo = useCallback( ( { lat, lng } ) => {
		mapInstance.panTo( { lat, lng } );
		mapInstance.setZoom( 14 );
	}, [ mapInstance ] );

	if ( loadError ) {
		return 'Error loading map';
	}

	if ( ! isLoaded ) {
		return 'Loading map...';
	}

	return (
		<>
			<header className="dealer-app__header">
				{ mapBounds && (
					<>
						<div className="dealer-heading__buttons"><Locate panTo={ panTo } />
							<a href="#dealer-heading__anchor"><button className="btn btn--red dealer-online__link">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor"><circle cx="12" cy="12" r="10"></circle><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"></path></svg>
								{ __( 'See online retailers', 'ankarsrum' ) }</button></a>
						</div>
						<Autocomplete bounds={ mapBounds } onLoad={ onLoad } onPlaceChanged={ onPlaceChanged } className="dealer-app__autocomplete">
							<input
								type="text"
								placeholder={ __( 'Enter a location', 'ankarsrum' ) }
							/>
						</Autocomplete>
					</>
				) }
			</header>
			<GoogleMap
				id="marker-example"
				mapContainerStyle={ mapContainerStyle }
				defaultZoom={ 3 }
				defaultCenter={ center }
				onLoad={ map => apiHasLoaded( map ) }
				onCenterChanged={ () => setCenter( mapInstance.getCenter().toJSON() ) }
				options={ {
					mapTypeControl: true,
					mapTypeControlOptions: {
						style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
						position: window.google.maps.ControlPosition.TOP_RIGHT,
					},
				} }
			>
				<MarkerClusterer options={ options }>{ renderClusterer }</MarkerClusterer>
			</GoogleMap>
			<Dealers center={ debouncedCenter } mapInstance={ mapInstance } mapAPI={ mapAPI } dealers={ dealers } />
		</>
	);
};

Map.defaultProps = {
	dealers: [
		{ id: 0, lat: -31.56391, lng: 147.154312 },
		{ id: 1, lat: -33.718234, lng: 150.363181 },
		{ id: 2, lat: -33.727111, lng: 150.371124 },
		{ id: 3, lat: -33.848588, lng: 151.209834 },
		{ id: 4, lat: -33.851702, lng: 151.216968 },
		{ id: 5, lat: -34.671264, lng: 150.863657 },
		{ id: 6, lat: -35.304724, lng: 148.662905 },
		{ id: 7, lat: -36.817685, lng: 175.699196 },
		{ id: 8, lat: -36.828611, lng: 175.790222 },
		{ id: 9, lat: -37.75, lng: 145.116667 },
		{ id: 10, lat: -37.759859, lng: 145.128708 },
		{ id: 11, lat: -37.765015, lng: 145.133858 },
		{ id: 12, lat: -37.770104, lng: 145.143299 },
		{ id: 13, lat: -37.7737, lng: 145.145187 },
		{ id: 14, lat: -37.774785, lng: 145.137978 },
		{ id: 15, lat: -37.819616, lng: 144.968119 },
		{ id: 16, lat: -38.330766, lng: 144.695692 },
		{ id: 17, lat: -39.927193, lng: 175.053218 },
		{ id: 18, lat: -41.330162, lng: 174.865694 },
		{ id: 19, lat: -42.734358, lng: 147.439506 },
		{ id: 20, lat: -42.734358, lng: 147.501315 },
		{ id: 21, lat: -42.735258, lng: 147.438 },
		{ id: 22, lat: -43.999792, lng: 170.463352 },
	],
	mapContainerStyle: {
		height: '380px',
		width: '100%',
	},
	options: {
		imagePath:
			'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
	},
	defaultCenter: {
		lat: 39.113766,
		lng: -94.692449,
	},
};

export default Map;
